// Jeu de présentation du simulateur local de l'API terrain, à la forme du contrat (@cc/contrats) :
// trois missions, une tournée de cinq points, un ordre de mission avec un formulaire de quatre points,
// positions fictives autour de Libreville. Identifiants lisibles pour les objets visés (mis-1, trn-1,
// om-1 : ils ne passent pas par les schémas des charges) ; UUID pour les points de collecte, que la
// charge d'une réalisation cite et que le schéma du contrat contrôle sur l'appareil.
import type { FormulaireTerrain, MissionTerrain, OrdreMissionTerrain, TourneeTerrain, TravailTerrain } from '@cc/contrats';

const pos = (longitude: number, latitude: number) => ({ longitude, latitude });

function dateIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Jour ISO (1 = lundi ... 7 = dimanche). */
function jourIso(d: Date): number {
  const j = d.getUTCDay();
  return j === 0 ? 7 : j;
}

export interface JeuSimulateur {
  cree_le: string;
  agent: TravailTerrain['agent'];
  missions: MissionTerrain[];
  tournees: TourneeTerrain[];
  ordres_mission: OrdreMissionTerrain[];
  referentiels: TravailTerrain['referentiels'];
  journal: Record<string, never>;
  photos: Record<string, never>;
  modifications: Record<string, string>;
  evenements: string[];
}

export function jeuDemonstration(aujourdhui = new Date()): JeuSimulateur {
  const jour = dateIso(aujourdhui);
  const fin = new Date(aujourdhui.getTime() + 7 * 86_400_000);
  const maintenant = aujourdhui.toISOString();
  const equipe = { id: 'eq-voirie-1', nom: 'Équipe voirie et éclairage', type: 'municipale' as const };
  const formulaire: FormulaireTerrain = {
    id: 'form-odp-1',
    code: 'ODP-TERRASSE',
    nom: "Contrôle d'une occupation du domaine public",
    version: 1,
    points: [
      { code: 'p1', libelle: "Surface occupée conforme à l'autorisation", aide: 'Mesurer la terrasse ; comparer à la surface autorisée.', photo_exigee: true, observation: 'facultative' },
      { code: 'p2', libelle: 'Passage des piétons laissé libre (1,50 m au moins)', photo_exigee: false, observation: 'facultative' },
      { code: 'p3', libelle: "Période d'occupation respectée", aide: "Dates et horaires de l'autorisation.", photo_exigee: false, observation: 'facultative' },
      { code: 'p4', libelle: 'Autorisation affichée sur place', photo_exigee: false, observation: 'aucune' },
    ],
  };
  const mission = (id: string, version: number, intervention: Omit<MissionTerrain['intervention'], 'etat' | 'service' | 'signalement'>, consignes: string): MissionTerrain => ({
    id,
    version,
    etat: 'affectee',
    date: jour,
    consignes,
    equipe,
    agent_appartenance_id: 'app-agent-1',
    intervention: { ...intervention, etat: 'planifiee', service: { id: 'svc-dst', nom: 'Direction des services techniques' }, signalement: null },
    rapports: [],
    modifiee_le: maintenant,
  });
  return {
    cree_le: maintenant,
    agent: { appartenance_id: 'app-agent-1', nom: 'Agent de démonstration', equipes: [{ ...equipe, role: 'chef' }], prestataire: false },
    missions: [
      mission('mis-1', 2, { id: 'int-12', numero: 'INT-2026-0012', titre: 'Lampadaire en panne, avenue de la Gare', categorie: 'eclairage', priorite: 'haute', description: 'Signalé par un usager du portail : le mât 14 est éteint depuis trois nuits.', adresse: 'Avenue de la Gare, quartier Louis (position fictive)', position: pos(9.4512, 0.3912) }, "Vérifier l'alimentation du mât 14, remplacer la lampe si nécessaire, photographier avant et après."),
      mission('mis-2', 1, { id: 'int-15', numero: 'INT-2026-0015', titre: 'Nid de poule, rue des Écoles', categorie: 'voirie', priorite: 'normale', description: null, adresse: 'Rue des Écoles, devant le lycée (position fictive)', position: pos(9.4588, 0.3977) }, 'Rebouchage à froid ; signaler si la chaussée est affaissée sur plus de deux mètres.'),
      mission('mis-3', 3, { id: 'int-9', numero: 'INT-2026-0009', titre: 'Dépôt sauvage derrière le marché', categorie: 'depot_sauvage', priorite: 'urgente', description: 'Gravats et déchets ménagers sur une dizaine de mètres carrés.', adresse: 'Arrière du marché central (position fictive)', position: pos(9.447, 0.386) }, 'Enlèvement avec la benne ; photographier le site avant et après.'),
    ],
    tournees: [
      {
        id: 'trn-1',
        code: 'CSN-MAR',
        nom: 'Collecte du secteur Nord, mardi',
        secteur: { id: 'sec-nord', nom: 'Secteur Nord' },
        jour_semaine: jourIso(aujourdhui),
        heure: '06:30',
        date: jour,
        operateur: 'commune',
        equipe: { id: equipe.id, nom: equipe.nom },
        vehicule: 'Benne GA-123-LB',
        points: [
          { id: '00000000-0000-4000-8000-000000000001', code: 'PC-GARE', nom: 'Bacs de la gare', type: 'bac', ordre: 1, position: pos(9.4505, 0.392) },
          { id: '00000000-0000-4000-8000-000000000002', code: 'PC-LYCEE', nom: 'Conteneur du lycée', type: 'conteneur', ordre: 2, position: pos(9.4582, 0.3972) },
          { id: '00000000-0000-4000-8000-000000000003', code: 'PC-MARCHE', nom: "Point d'apport du marché", type: 'point_apport', ordre: 3, position: pos(9.4475, 0.3865) },
          { id: '00000000-0000-4000-8000-000000000004', code: 'PC-POSTE', nom: 'Bacs de la poste', type: 'bac', ordre: 4, position: pos(9.4531, 0.3889) },
          { id: '00000000-0000-4000-8000-000000000005', code: 'PC-STADE', nom: 'Conteneur du stade', type: 'conteneur', ordre: 5, position: pos(9.4621, 0.3934) },
        ],
        version: 1,
        realisation: null,
      },
    ],
    ordres_mission: [
      {
        id: 'om-1',
        numero: 'OM-2026-0004',
        objet_type: 'autorisation_domaine_public',
        objet_id: 'adp-21',
        objet_libelle: 'Terrasse du café du Rond-point (autorisation ODP-2026-0021, 18 m²)',
        lieu: 'Rond-point de la Poste (position fictive)',
        position: pos(9.4535, 0.3892),
        periode_debut: jour,
        periode_fin: dateIso(fin),
        consignes: 'Contrôle sur place en matinée ; relever la position et photographier la terrasse.',
        formulaire,
        version: 1,
        etat: 'emis',
      },
    ],
    referentiels: {
      categories: [
        { code: 'eclairage', libelle: 'Éclairage public' },
        { code: 'voirie', libelle: 'Voirie et chaussée' },
        { code: 'depot_sauvage', libelle: 'Dépôt sauvage' },
        { code: 'assainissement', libelle: 'Assainissement et eaux' },
        { code: 'espaces_verts', libelle: 'Espaces verts et arbres' },
      ],
      priorites: [
        { code: 'basse', libelle: 'Basse' },
        { code: 'normale', libelle: 'Normale' },
        { code: 'haute', libelle: 'Haute' },
        { code: 'urgente', libelle: 'Urgente' },
      ],
      formulaires: [formulaire],
    },
    journal: {},
    photos: {},
    modifications: {},
    evenements: [],
  };
}
